// What the email-code page shows while the account is being made (decided 2026-10-28).
//
// A right code creates the account in the same request, and on integration that request takes
// up to ~18 s. Instead of boxes that grey out for the whole wait: the work as a short checklist.
// Its steps tick on a timer, since the server reports nothing midway, and the last one ticks
// when the answer arrives (`done`). No percentage: a bar would claim to know how far along it is.
// Under it, "Continuing…", centred and turning like "Checking…", for the whole wait; after 10 s,
// a line that says it's still going.
//
// Shown only once the answer is slow (SignupVerifyEmailPage, CHECKLIST_AFTER_MS): a wrong code is
// answered quickly, so by then the code was right, and "Code verified" is already true.

import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import i18next from "i18next";
import BusyLabel from "./BusyLabel";

// Every time counts from the code going in (`since`), not from the checklist appearing.
/** When "Creating your account" ticks and "Saving your spot" starts. */
export const ACCOUNT_STEP_MS = 6000;
/** When the page says it is still working. */
export const SLOW_NOTE_MS = 10000;

type StepState = "done" | "active" | "waiting";

interface Props {
  /** The answer is in: every step ticks. */
  done: boolean;
  /** When the code went in (Date.now()); the checklist's mount when absent. */
  since?: number;
}

const AccountSetupProgress: React.FC<Props> = ({ done, since }) => {
  const [started] = useState(() => since || Date.now());
  const [elapsed, setElapsed] = useState(() => Date.now() - started);

  useEffect(() => {
    const timer = setInterval(() => setElapsed(Date.now() - started), 250);
    return () => clearInterval(timer);
  }, [started]);

  const accountDone = done || elapsed >= ACCOUNT_STEP_MS;
  const steps: { label: string; state: StepState }[] = [
    { label: i18next.t("Code verified"), state: "done" },
    { label: i18next.t("Creating your account"), state: accountDone ? "done" : "active" },
    { label: i18next.t("Saving your spot"), state: done ? "done" : accountDone ? "active" : "waiting" },
  ];

  return (
    <Wrap aria-busy={!done}>
      <Title role="status">{i18next.t("Setting up your account")}</Title>
      <List>
        {steps.map((step) => (
          <Step key={step.label} state={step.state}>
            <Mark state={step.state} aria-hidden="true">
              {step.state === "done" ? "✓" : null}
            </Mark>
            <span>{step.label}</span>
          </Step>
        ))}
      </List>
      <ContinuingWrap>
        <BusyLabel>{i18next.t("Continuing…")}</BusyLabel>
      </ContinuingWrap>
      {!done && elapsed >= SLOW_NOTE_MS && (
        <Slow role="status">{i18next.t("Still working, this can take a few more seconds.")}</Slow>
      )}
    </Wrap>
  );
};

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Wrap = styled.div`
  max-width: 340px;
  margin: 48px auto 0;
  text-align: left;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #122640;
  margin: 0 0 24px;
  text-align: center;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Step = styled.li<{ state: StepState }>`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 15px;
  line-height: 20px;
  font-weight: ${(p) => (p.state === "active" ? 600 : 400)};
  color: ${(p) => (p.state === "waiting" ? "#8a94a6" : "#122640")};
  transition: color 0.2s;
`;

// Done: a green tick. Active: a turning ring, still for less motion. Waiting: an empty circle.
const Mark = styled.span<{ state: StepState }>`
  flex: none;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #ffffff;
  background: ${(p) => (p.state === "done" ? "#0bb07b" : "transparent")};
  border: 2px solid ${(p) => (p.state === "done" ? "#0bb07b" : p.state === "active" ? "#cfe9fb" : "#e3e8ee")};
  border-top-color: ${(p) => (p.state === "done" ? "#0bb07b" : p.state === "active" ? "#0f9bf0" : "#e3e8ee")};
  animation: ${(p) => (p.state === "active" ? `${spin} 0.9s linear infinite` : "none")};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ContinuingWrap = styled.div`
  margin-top: 28px;
`;

const Slow = styled.p`
  margin: 12px 0 0;
  font-size: 13px;
  color: #53627c;
  text-align: center;
`;

export default AccountSetupProgress;
